/**
 * Copyright 2022-2025, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __awaiter } from "tslib";
import { OptimizelySegmentOption } from './optimizely_segment_option';
import { ODP_CONFIG_NOT_AVAILABLE, ODP_NOT_INTEGRATED } from 'error_message';
export const LOGGER_NAME = 'OdpSegmentManager';
export class DefaultOdpSegmentManager {
    constructor(segmentsCache, odpSegmentApiManager, logger) {
        this.segmentsCache = segmentsCache;
        this.odpSegmentApiManager = odpSegmentApiManager;
        if (logger) {
            this.setLogger(logger);
        }
    }
    setLogger(logger) {
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
        this.odpSegmentApiManager.setLogger(logger.child());
    }
    /**
     * Attempts to fetch and return a list of a user's qualified segments from the local segments cache.
     * If no cached data exists for the target user, this fetches and caches data from the ODP server instead.
     * @param userKey Key used for identifying the id type.
     * @param userValue The id value itself.
     * @param options An array of OptimizelySegmentOption used to ignore and/or reset the cache.
     * @returns Qualified segments for the user from the cache or the ODP server if the cache is empty.
     */
    fetchQualifiedSegments(userKey, userValue, options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.odpIntegrationConfig) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn(ODP_CONFIG_NOT_AVAILABLE);
                return null;
            }
            if (!this.odpIntegrationConfig.integrated) {
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.warn(ODP_NOT_INTEGRATED);
                return null;
            }
            const odpConfig = this.odpIntegrationConfig.odpConfig;
            const segmentsToCheck = odpConfig.segmentsToCheck;
            if (!segmentsToCheck || segmentsToCheck.length <= 0) {
                return [];
            }
            const cacheKey = this.makeCacheKey(userKey, userValue);
            const ignoreCache = options === null || options === void 0 ? void 0 : options.includes(OptimizelySegmentOption.IGNORE_CACHE);
            const resetCache = options === null || options === void 0 ? void 0 : options.includes(OptimizelySegmentOption.RESET_CACHE);
            if (resetCache) {
                this.segmentsCache.reset();
            }
            if (!ignoreCache) {
                const cachedSegments = yield this.segmentsCache.lookup(cacheKey);
                if (cachedSegments) {
                    return cachedSegments;
                }
            }
            const segments = yield this.odpSegmentApiManager.fetchSegments(odpConfig.apiKey, odpConfig.apiHost, userKey, userValue, segmentsToCheck);
            if (segments && !ignoreCache) {
                this.segmentsCache.save(cacheKey, segments);
            }
            return segments;
        });
    }
    makeCacheKey(userKey, userValue) {
        return `${userKey}-$-${userValue}`;
    }
    updateConfig(config) {
        this.odpIntegrationConfig = config;
        this.segmentsCache.reset();
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=odp_segment_manager.js.map